import React, { useMemo, useState } from 'react';
import {
    Box,
    Tabs,
    Tab,
    ButtonGroup,
    Button,
    Typography,
    createTheme,
    ThemeProvider,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useLocation } from 'react-router-dom';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { useFarmData } from '../context/FarmDataContext';

const FinancePage = () => {
    const { farmData } = useFarmData();
    const location = useLocation();
    const zones = farmData?.zones || [];
    const [tabIndex, setTabIndex] = useState(0);
    const [viewMode, setViewMode] = useState('yearly'); // 'yearly' or 'monthly'
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    // Get location filters from URL state
    const section_id = location.state?.section_id;
    const plot_id = location.state?.plot_id;
    const crop_id = location.state?.crop_id;

    const darkTheme = createTheme({
        palette: {
            mode: 'dark',
            primary: { main: '#90caf9' },
            secondary: { main: '#f48fb1' },
            background: {
                default: '#121212',
                paper: '#1e1e1e',
            },
            text: {
                primary: '#ffffff',
                secondary: '#b0b0b0',
            },
        },
    });

    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const handleViewModeChange = (mode) => {
        setViewMode(mode);
    };

    const formatTimeKey = (date) => {
        const d = new Date(date);
        return viewMode === 'yearly' ? d.getFullYear() : `${d.getFullYear()}-${d.getMonth() + 1}`;
    };

    // Helper function to format numbers to two decimal places
    const formatToTwoDecimals = (value) => {
        if (typeof value !== 'number') return value;
        return Number(value.toFixed(2));
    };

    // Helper function to filter data by location
    const filterByLocation = (data) => {
        if (!data) return [];
        return data.filter(item => {
            // First check if item is deleted
            if (item.deleted) return false;
            
            // Check crop filter first (highest priority)
            if (crop_id) {
                // For harvests and sales, check if they belong to the selected crop
                if (item.crop_id && item.crop_id !== crop_id) return false;
                // For sales, check if the harvest belongs to the selected crop
                if (item.harvest_id) {
                    const harvest = farmData?.harvests?.find(h => h.id === item.harvest_id);
                    if (harvest && harvest.crop_id !== crop_id) return false;
                }
            }
            
            // Check location filters
            if (plot_id) {
                if (item.plot_id !== plot_id) return false;
            } else if (section_id) {
                if (item.zone_id !== section_id) return false;
            }

            return true;
        });
    };

    // Helper function to filter crops by location and date range (similar to FarmDetails)
    const filterCropsByLocation = (crops) => {
        if (!crops) return [];
        return crops.filter(crop => {
            // First check if item is deleted
            if (crop.deleted) return false;
            
            // Check crop filter first (highest priority)
            if (crop_id) {
                if (crop.id !== crop_id) return false;
            }
            
            // Check location filters
            if (plot_id) {
                if (crop.plot_id !== plot_id) return false;
            } else if (section_id) {
                if (crop.zone_id !== section_id) return false;
            }

            return true;
        });
    };

    const profitLossData = useMemo(() => {
        if (!farmData) return { rows: [], columns: [] };
    
        const groupedData = {};
        const categories = [
            'Harvest',
            'Sales',
            'Seed Cost',
            'Fertilizer Cost',
            'Fungicide Cost',
            'Insecticide Cost',
            'Herbicide Cost',
            'Total Costs',
            'Gross Profit',
            'Gross Profit %',
        ];
    
        const addRecord = (timeKey, category, amount, record) => {
            if (!groupedData[category]) {
                groupedData[category] = {};
            }
            if (!groupedData[category][timeKey]) {
                groupedData[category][timeKey] = { total: 0, records: [] };
            }
            groupedData[category][timeKey].total += amount;
            groupedData[category][timeKey].records.push(record);
        };
    
        // Process data by category with location filtering
        filterByLocation(farmData.harvests).forEach((harvest) => {
            const timeKey = formatTimeKey(harvest.date);
            addRecord(timeKey, 'Harvest', harvest.harvest_amt || 0, {
                amount: harvest.harvest_amt || 0,
                zone_id: harvest.zone_id,
                plot_id: harvest.plot_id,
                crop_id: harvest.crop_id,
            });
        });
    
        filterByLocation(farmData.sales).forEach((sale) => {
            const timeKey = formatTimeKey(sale.date);
            addRecord(timeKey, 'Sales', sale.sale_amt || 0, {
                amount: sale.sale_amt || 0,
                zone_id: sale.zone_id,
                plot_id: sale.plot_id,
                harvest_id: sale.harvest_id,
            });
        });
    
        ['fertilizerEvents', 'fungicideEvents', 'insecticideEvents', 'herbicideEvents'].forEach((eventType, idx) => {
            const category = categories[3 + idx];
            filterByLocation(farmData[eventType]).forEach((event) => {
                const timeKey = formatTimeKey(event.date);
                addRecord(timeKey, category, event.event_cost || 0, {
                    amount: event.event_cost || 0,
                    zone_id: event.zone_id,
                    plot_id: event.plot_id,
                });
            });
        });
    
        filterCropsByLocation(farmData.crops).forEach((crop) => {
            const timeKey = formatTimeKey(crop.transplant_date || crop.planting_date);
            const seedCost = crop.seed_cost || 0;
            addRecord(timeKey, 'Seed Cost', seedCost, {
                amount: seedCost,
                zone_id: crop.zone_id,
                plot_id: crop.plot_id,
                crop_id: crop.id,
            });
        });
    
        // Calculate totals (no zone filtering needed since data is already filtered)
        const rows = categories.map((category, idx) => {
            const row = { id: idx + 1, category };
    
            Object.keys(groupedData[category] || {}).forEach((timeKey) => {
                const records = groupedData[category][timeKey].records;
                row[timeKey] = formatToTwoDecimals(records.reduce((sum, record) => sum + (record.amount || 0), 0));
            });
    
            return row;
        });
    
        // Calculate "Gross Profit" and "Gross Profit %"
        const salesByTime = groupedData['Sales'];
        const costCategories = [
            'Fertilizer Cost',
            'Fungicide Cost',
            'Insecticide Cost',
            'Herbicide Cost',
            'Seed Cost',
        ];
    

    
        // Get all unique time keys from all categories, not just Sales
        const allTimeKeys = new Set();
        Object.keys(groupedData).forEach(category => {
            Object.keys(groupedData[category] || {}).forEach(timeKey => {
                allTimeKeys.add(timeKey);
            });
        });
        
        // Sort columns by date (yearly or monthly)
        let sortedColumns = Array.from(allTimeKeys).sort((a, b) => {
            if (viewMode === 'yearly') {
                return parseInt(a) - parseInt(b);
            } else {
                // For monthly view, sort by year-month
                const [aYear, aMonth] = a.split('-').map(Number);
                const [bYear, bMonth] = b.split('-').map(Number);
                
                if (aYear !== bYear) {
                    return aYear - bYear;
                }
                return aMonth - bMonth;
            }
        });

        // For monthly view, add yearly total columns
        if (viewMode === 'monthly') {
            const yearlyTotals = new Set();
            sortedColumns.forEach(timeKey => {
                if (timeKey.includes('-')) {
                    const year = timeKey.split('-')[0];
                    yearlyTotals.add(year);
                }
            });
            
            // Add yearly total columns after the monthly columns
            const yearlyColumns = Array.from(yearlyTotals).sort((a, b) => parseInt(a) - parseInt(b));
            sortedColumns = [...sortedColumns, ...yearlyColumns];
        }

        // Calculate "Total Costs", "Gross Profit" and "Gross Profit %" for ALL time keys, not just sales
        sortedColumns.forEach((timeKey) => {
            const salesRecords = groupedData['Sales']?.[timeKey]?.records || [];
            const totalSales = salesRecords.reduce((sum, record) => sum + (record.amount || 0), 0);
    
            const totalCosts = costCategories
                .map((category) => {
                    const costRecords = groupedData[category]?.[timeKey]?.records || [];
                    return costRecords.reduce((sum, record) => sum + (record.amount || 0), 0);
                })
                .reduce((a, b) => a + b, 0);
    
            const grossProfit = totalSales - totalCosts;
            const grossProfitPercentage = totalSales > 0 ? (grossProfit / totalSales) * 100 : 0;
    
            // Add computed values to rows
            const totalCostsRow = rows.find((row) => row.category === 'Total Costs');
            const grossProfitRow = rows.find((row) => row.category === 'Gross Profit');
            const grossProfitPercentageRow = rows.find((row) => row.category === 'Gross Profit %');
    
            if (totalCostsRow) totalCostsRow[timeKey] = formatToTwoDecimals(totalCosts);
            if (grossProfitRow) grossProfitRow[timeKey] = formatToTwoDecimals(grossProfit);
            if (grossProfitPercentageRow) grossProfitPercentageRow[timeKey] = formatToTwoDecimals(grossProfitPercentage);
        });

        // For monthly view, calculate yearly totals for each category
        if (viewMode === 'monthly') {
            const yearlyTotals = new Set();
            sortedColumns.forEach(timeKey => {
                if (timeKey.includes('-')) {
                    const year = timeKey.split('-')[0];
                    yearlyTotals.add(year);
                }
            });

            // Calculate yearly totals for each category
            rows.forEach(row => {
                Array.from(yearlyTotals).forEach(year => {
                    const monthlyKeys = sortedColumns.filter(key => key.startsWith(year + '-'));
                    const yearlyTotal = monthlyKeys.reduce((sum, monthKey) => {
                        return sum + (row[monthKey] || 0);
                    }, 0);
                    row[year] = formatToTwoDecimals(yearlyTotal);
                });
            });
        }

        
        // Debug: Log the data to see what's happening
        console.log('FinancePage Debug:', {
            viewMode,
            groupedDataKeys: Object.keys(groupedData),
            salesKeys: Object.keys(groupedData['Sales'] || {}),
            seedCostKeys: Object.keys(groupedData['Seed Cost'] || {}),
            fertilizerKeys: Object.keys(groupedData['Fertilizer Cost'] || {}),
            allTimeKeys: sortedColumns,
            sampleSeedData: groupedData['Seed Cost'] ? Object.keys(groupedData['Seed Cost']).map(key => ({
                timeKey: key,
                total: groupedData['Seed Cost'][key].total,
                recordCount: groupedData['Seed Cost'][key].records.length
            })) : []
        });
        
        return { rows, columns: sortedColumns, groupedData };
    }, [farmData, viewMode, section_id, plot_id, crop_id]);
    

    const renderDetailPanel = ({ row }) => {
        const timeKeys = Object.keys(profitLossData.groupedData[row.category] || {}).sort((a, b) => {
            const [aYear, aMonth] = a.split('-').map(Number);
            const [bYear, bMonth] = b.split('-').map(Number);

            if (aYear !== bYear) {
                return aYear - bYear; // Sort by year
            }
            return (aMonth || 0) - (bMonth || 0); // Sort by month if years are equal
        });

        const records = timeKeys.flatMap(
            (timeKey) => profitLossData.groupedData[row.category]?.[timeKey]?.records || []
        );

        let detailRows = [];
        let detailColumns = [];

        if (crop_id) {
            // Show crop details - group by crop variety and plant type
            const cropTotals = records.reduce((acc, record) => {
                let cropId = record.crop_id;
                
                // For sales, get crop_id from harvest
                if (record.harvest_id) {
                    const harvest = farmData?.harvests?.find(h => h.id === record.harvest_id);
                    cropId = harvest?.crop_id;
                }
                
                if (!cropId) return acc;

                const crop = farmData?.crops?.find(c => c.id === cropId);
                if (!crop) return acc;

                const cropLabel = `${crop.variety_name || 'Unknown'} - ${crop.plant_type || 'Unknown'}`;

                if (!acc[cropLabel]) {
                    acc[cropLabel] = {};
                }

                timeKeys.forEach((timeKey) => {
                    if (!acc[cropLabel][timeKey]) {
                        acc[cropLabel][timeKey] = 0;
                    }
                    if (profitLossData.groupedData[row.category]?.[timeKey]?.records.includes(record)) {
                        acc[cropLabel][timeKey] = formatToTwoDecimals(acc[cropLabel][timeKey] + (record.amount || 0));
                    }
                });

                return acc;
            }, {});

            detailRows = Object.entries(cropTotals).map(([cropLabel, totals], index) => ({
                id: index + 1,
                crop_label: cropLabel,
                ...totals,
            }));

            detailColumns = [
                { field: 'crop_label', headerName: 'Crop', flex: 1, minWidth: 200 },
                ...timeKeys.map((timeKey) => ({
                    field: timeKey,
                    headerName: timeKey,
                    flex: 1,
                    minWidth: 120,
                })),
            ];
        } else if (plot_id) {
            // Show crop totals for the selected plot
            const cropTotals = records.reduce((acc, record) => {
                let cropId = record.crop_id;
                
                // For sales, get crop_id from harvest
                if (record.harvest_id) {
                    const harvest = farmData?.harvests?.find(h => h.id === record.harvest_id);
                    cropId = harvest?.crop_id;
                }
                
                if (!cropId) return acc;

                const crop = farmData?.crops?.find(c => c.id === cropId);
                if (!crop) return acc;

                const cropLabel = `${crop.variety_name || 'Unknown'} - ${crop.plant_type || 'Unknown'}`;
                
                if (!acc[cropLabel]) {
                    acc[cropLabel] = {};
                }

                timeKeys.forEach((timeKey) => {
                    if (!acc[cropLabel][timeKey]) {
                        acc[cropLabel][timeKey] = 0;
                    }
                    if (profitLossData.groupedData[row.category]?.[timeKey]?.records.includes(record)) {
                        acc[cropLabel][timeKey] = formatToTwoDecimals(acc[cropLabel][timeKey] + (record.amount || 0));
                    }
                });

                return acc;
            }, {});

            detailRows = Object.entries(cropTotals).map(([cropLabel, totals], index) => ({
                id: index + 1,
                crop_label: cropLabel,
                ...totals,
            }));

            detailColumns = [
                { field: 'crop_label', headerName: 'Crop', flex: 1, minWidth: 200 },
                ...timeKeys.map((timeKey) => ({
                    field: timeKey,
                    headerName: timeKey,
                    flex: 1,
                    minWidth: 120,
                })),
            ];
        } else if (section_id) {
            // Show plot totals for the selected section
            const plotTotals = records.reduce((acc, record) => {
                const plotId = record.plot_id;
                if (!plotId) return acc;

                // Find plot name from zones data
                const plot = zones.flatMap(zone => zone.plots || []).find(p => p.id === plotId);
                const plotName = plot?.name || `Plot ${plotId}`;
                
                if (!acc[plotName]) {
                    acc[plotName] = {};
                }

                timeKeys.forEach((timeKey) => {
                    if (!acc[plotName][timeKey]) {
                        acc[plotName][timeKey] = 0;
                    }
                    if (profitLossData.groupedData[row.category]?.[timeKey]?.records.includes(record)) {
                        acc[plotName][timeKey] = formatToTwoDecimals(acc[plotName][timeKey] + (record.amount || 0));
                    }
                });

            return acc;
        }, {});

            detailRows = Object.entries(plotTotals).map(([plotName, totals], index) => ({
            id: index + 1,
                plot_name: plotName,
            ...totals,
        }));

            detailColumns = [
                { field: 'plot_name', headerName: 'Plot', flex: 1, minWidth: 150 },
            ...timeKeys.map((timeKey) => ({
                field: timeKey,
                headerName: timeKey,
                    flex: 1,
                    minWidth: 120,
            })),
        ];
        } else {
            // Show zone totals when no specific location is selected
            const zoneTotals = records.reduce((acc, record) => {
                const { zone_id, amount } = record;
                if (!zone_id) return acc;

                const zoneName = zones.find(zone => zone.id === zone_id)?.name || `Section ${zone_id}`;
                
                if (!acc[zoneName]) {
                    acc[zoneName] = {};
                }

                timeKeys.forEach((timeKey) => {
                    if (!acc[zoneName][timeKey]) {
                        acc[zoneName][timeKey] = 0;
                    }
                    if (profitLossData.groupedData[row.category]?.[timeKey]?.records.includes(record)) {
                        acc[zoneName][timeKey] = formatToTwoDecimals(acc[zoneName][timeKey] + (record.amount || 0));
                    }
                });

                return acc;
            }, {});

            detailRows = Object.entries(zoneTotals).map(([zoneName, totals], index) => ({
                id: index + 1,
                zone_name: zoneName,
                ...totals,
            }));

            detailColumns = [
                { field: 'zone_name', headerName: 'Section', flex: 1, minWidth: 150 },
                ...timeKeys.map((timeKey) => ({
                    field: timeKey,
                    headerName: timeKey,
                    flex: 1,
                    minWidth: 120,
                })),
            ];
        }

        const getDetailTitle = () => {
            if (crop_id) return 'Crop Details';
            if (plot_id) return 'Crop Totals by Plot';
            if (section_id) return 'Plot Totals by Section';
            return 'Section Totals';
        };

        return (
            <Box sx={{ 
                p: { xs: 2, md: 3 }, 
                bgcolor: '#181F2A', 
                overflow: 'auto',
                borderRadius: 1.5,
                boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)',
                m: { xs: 1, md: 2 }
            }}>
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 500, mb: 2 }}>
                    {getDetailTitle()}
                </Typography>
                <DataGridPro
                    rows={detailRows}
                    columns={detailColumns}
                    autoHeight
                    slots={{ toolbar: GridToolbar }}
                    pagination
                    sx={{
                        bgcolor: 'transparent',
                        color: '#fff',
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#222C2F !important',
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: 13,
                            borderBottom: '1px solid rgba(255,255,255,0.12)',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            bgcolor: '#222C2F !important',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: 13,
                        },
                        '& .MuiDataGrid-cell': {
                            bgcolor: 'transparent',
                            color: '#fff',
                            fontSize: 12,
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                            py: 0.5,
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            bgcolor: 'transparent',
                        },
                        '& .MuiDataGrid-row:nth-of-type(odd)': {
                            bgcolor: 'rgba(255,255,255,0.02)',
                        },
                        '& .MuiDataGrid-row:nth-of-type(even)': {
                            bgcolor: 'transparent',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: 'rgba(255,255,255,0.04)',
                        },
                        '& .MuiDataGrid-toolbarContainer button, & .MuiDataGrid-toolbarContainer .MuiTypography-root': {
                            color: '#F9AA33',
                        },
                    }}
                />
            </Box>
        );
    };

    const renderTable = (data) => (
        <Box sx={{ height: { xs: 'auto', md: 600 }, mt: 2, minHeight: { xs: 400, md: 600 } }}>
            <DataGridPro
                rows={data.rows}
                columns={[
                    { field: 'category', headerName: 'Category', flex: 1, minWidth: 150 },
                    ...data.columns.map((col) => ({
                        field: `${col}`,
                        headerName: `${col}`,
                        flex: 1,
                        minWidth: 120,
                    })),
                ]}
                sx={{
                    height: { xs: 'auto', md: '100%' },
                    bgcolor: 'transparent',
                    color: '#fff',
                    border: 'none',
                    '& .MuiDataGrid-columnHeaders': {
                        bgcolor: '#222C2F !important',
                        color: '#fff',
                        fontWeight: 500,
                        fontSize: { xs: 12, md: 13 },
                        borderBottom: '1px solid rgba(255,255,255,0.12)',
                    },
                    '& .MuiDataGrid-columnHeader': {
                        bgcolor: '#222C2F !important',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                        color: '#fff',
                        fontWeight: 500,
                        fontSize: { xs: 12, md: 13 },
                    },
                    '& .MuiDataGrid-cell': {
                        bgcolor: 'transparent',
                        color: '#fff',
                        fontSize: { xs: 11, md: 12 },
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                        py: 0.5,
                    },
                    '& .MuiDataGrid-virtualScroller': {
                        bgcolor: 'transparent',
                    },
                    '& .MuiDataGrid-row': {
                        height: { xs: 40, md: 48 },
                    },
                    '& .MuiDataGrid-row:nth-of-type(odd)': {
                        bgcolor: 'rgba(255,255,255,0.02)',
                    },
                    '& .MuiDataGrid-row:nth-of-type(even)': {
                        bgcolor: 'transparent',
                    },
                    '& .MuiDataGrid-row:hover': {
                        bgcolor: 'rgba(255,255,255,0.04)',
                    },
                    '& .MuiDataGrid-toolbarContainer button, & .MuiDataGrid-toolbarContainer .MuiTypography-root': {
                        color: '#F9AA33',
                    },
                    '& .MuiDataGrid-footerContainer': {
                        bgcolor: '#222C2F',
                        color: '#fff',
                        borderTop: '1px solid rgba(255,255,255,0.12)',
                    },
                }}
                autoHeight
                slots={{ toolbar: GridToolbar }}
                pagination
                getDetailPanelContent={renderDetailPanel}
                getDetailPanelHeight={() => 300}
                expandDetailPanelByDefault={false}
            />
        </Box>
    );

    return (
        <ThemeProvider theme={darkTheme}>
            <Box sx={{ 
                p: { xs: '0 0px 0px 0px', md: 3 }, 
                bgcolor: '#181F2A', 
                color: '#fff', 
                height: '100%', 
                width: '100%', 
                overflow: 'auto',
                mt: { xs: 0, md: -2 },
                mb: { xs: 8, md: 0 },
            }}>
                <Box sx={{ 
                    backgroundColor: '#181F2A', 
                    borderRadius: 1.5, 
                    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)', 
                    p: { xs: '0px 0px 0px 0px', md: '8px 24px 24px 24px' },
                    height: { xs: 'auto', md: '100%' },
                    overflow: { xs: 'visible', md: 'hidden' },
                }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} sx={{ 
                        flexDirection: { xs: 'column', md: 'row' }, 
                        gap: { xs: 1, md: 0 },
                        '& .MuiButtonGroup-root': {
                            order: { xs: 1, md: 2 },
                            flexShrink: 0,
                            paddingTop: '8px'
                        },
                        '& .MuiTabs-root': {
                            order: { xs: 2, md: 1 },
                            flex: 1,
                            width: '100%'
                        }
                    }}>
                    <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        textColor="primary"
                        indicatorColor="primary"
                        fullWidth
                        sx={{
                            width: '100%',
                            '& .MuiTab-root': {
                                color: '#AAB4C8',
                                fontSize: { xs: 12, md: 14 },
                                fontWeight: 500,
                                flex: 1,
                                minWidth: 0,
                                '&.Mui-selected': {
                                    color: '#F9AA33',
                                },
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#F9AA33',
                            },
                        }}
                    >
                        <Tab label="Profit & Loss" />
                            <Tab label="Cash Flow" />
                        <Tab label="Balance Sheet" />
                    </Tabs>
                        <ButtonGroup size={isMobile ? "small" : "medium"}>
                        <Button
                            variant={viewMode === 'yearly' ? 'contained' : 'outlined'}
                            onClick={() => handleViewModeChange('yearly')}
                                sx={{
                                    bgcolor: viewMode === 'yearly' ? '#F9AA33' : 'transparent',
                                    color: viewMode === 'yearly' ? '#181F2A' : '#AAB4C8',
                                    borderColor: '#AAB4C8',
                                    '&:hover': {
                                        bgcolor: viewMode === 'yearly' ? '#F9AA33' : 'rgba(255,255,255,0.08)',
                                    },
                                }}
                        >
                            Yearly
                        </Button>
                        <Button
                            variant={viewMode === 'monthly' ? 'contained' : 'outlined'}
                            onClick={() => handleViewModeChange('monthly')}
                                sx={{
                                    bgcolor: viewMode === 'monthly' ? '#F9AA33' : 'transparent',
                                    color: viewMode === 'monthly' ? '#181F2A' : '#AAB4C8',
                                    borderColor: '#AAB4C8',
                                    '&:hover': {
                                        bgcolor: viewMode === 'monthly' ? '#F9AA33' : 'rgba(255,255,255,0.08)',
                                    },
                                }}
                        >
                            Monthly
                        </Button>
                    </ButtonGroup>
                </Box>
                    {tabIndex === 0 && renderTable(profitLossData)}
                </Box>
            </Box>
        </ThemeProvider>
    );
};

export default FinancePage;
